import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaBriefcase, FaMedal, FaGraduationCap } from 'react-icons/fa';

const Experience = ({ darkMode }) => {
    const { t } = useTranslation();

    // Datos predeterminados en caso de que las traducciones no estén disponibles
    const defaultExperienceData = [
        {
            id: 1,
            title: "AI Specialist",
            company: "Hytech",
            period: "2024 - Presente",
            description: "Especialista en Inteligencia Artificial desarrollando soluciones empresariales innovadoras. Desarrollo de plataforma de chatbot para CRM que permite búsquedas en base de datos MySQL mediante lenguaje natural, utilizando LangChain con tools personalizadas para consultas SQL automáticas. Creación de software de reportes con IA para análisis y generación automática de informes. Implementación con Streamlit para interfaces interactivas y FastAPI para servicios backend robustos.",
            type: "work",
            technologies: ["Python", "LangChain", "OpenAI", "LLMs", "RAG", "AI Agents", "MySQL", "Streamlit", "FastAPI", "SQL Tools", "Prompt Engineering"]
        },
        {
            id: 2,
            title: "Desarrollador Web",
            company: "Click Ping",
            period: "2024",
            description: "Desarrollador web con experiencia en Django (Python), creando proyectos como chatbots para Telegram (Aiogram) y WhatsApp (Meta Flow). Participación en el modelado de bases de datos y la configuración del panel de administración de Django.",
            type: "work",
            technologies: ["Python", "Django", "Aiogram", "Meta Flow", "PostgreSQL"]
        }
    ];

    const defaultEducationData = [
        {
            id: 1,
            title: "Ingeniería en Sistemas",
            institution: "Pontificia Universidad Católica Madre y Maestra (PUCMM)",
            period: "Graduado - 2024",
            description: "Título obtenido en Ingeniería en Sistemas con formación integral en desarrollo de software, bases de datos, redes, seguridad informática y gestión de proyectos tecnológicos.",
            type: "education"
        },
        {
            id: 2,
            title: "Estudios Secundarios",
            institution: "Politécnico Ernesto Disla Nuñez",
            period: "Graduado",
            description: "Formación en ciencias y tecnología.",
            type: "education"
        }
    ];

    const defaultAchievementsData = [
        {
            id: 1,
            title: "Final Latinoamericana ICPC 2022",
            description: "Participante en la International Collegiate Programming Contest (ICPC), calificando a nivel regional y avanzando a la Final Latinoamericana 2022, compitiendo con países como Cuba, Rusia, entre otros.",
            type: "achievement"
        }
    ];

    // Obtener datos de experiencia desde las traducciones o usar valores predeterminados
    let experienceData;
    let educationData;
    let achievementsData;

    try {
        const translatedExperience = t('experience.workItems', { returnObjects: true });
        experienceData = Array.isArray(translatedExperience) ? translatedExperience : defaultExperienceData;
        console.log('🔍 DATOS DE EXPERIENCIA CARGADOS:', experienceData);
    } catch (error) {
        console.error("Error al obtener datos de experiencia:", error);
        experienceData = defaultExperienceData;
        console.log('⚠️ USANDO DATOS POR DEFECTO:', experienceData);
    }

    try {
        const translatedEducation = t('experience.educationItems', { returnObjects: true });
        educationData = Array.isArray(translatedEducation) ? translatedEducation : defaultEducationData;
    } catch (error) {
        console.error("Error al obtener datos de educación:", error);
        educationData = defaultEducationData;
    }

    try {
        const translatedAchievements = t('experience.achievementItems', { returnObjects: true });
        achievementsData = Array.isArray(translatedAchievements) ? translatedAchievements : defaultAchievementsData;
    } catch (error) {
        console.error("Error al obtener datos de logros:", error);
        achievementsData = defaultAchievementsData;
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5 }
        }
    };

    return (
        <section id="experience" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {t('experience.title')}
            </h2>

            <div className="space-y-12">
                {/* Experiencia Laboral */}
                <div>
                    <h3 className={`text-2xl font-semibold mb-4 flex items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <FaBriefcase className={`mr-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        {t('experience.sections.work')}
                    </h3>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {experienceData.map(experience => (
                            <motion.div
                                key={experience.id}
                                variants={itemVariants}
                                className={`rounded-lg p-5 border-l-4 ${
                                    darkMode
                                        ? 'bg-gray-700 border-cyan-400'
                                        : 'bg-gray-50 border-cyan-500'
                                }`}
                            >
                                <div className="flex flex-col md:flex-row md:justify-between mb-2">
                                    <h4 className="text-xl font-bold">{experience.title}</h4>
                                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {experience.period}
                                    </span>
                                </div>

                                <p className={`text-lg mb-2 ${darkMode ? 'text-cyan-300' : 'text-cyan-600'}`}>
                                    {experience.company}
                                </p>

                                <p className={`mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {experience.description}
                                </p>

                                {experience.technologies && (
                                    <div className="flex flex-wrap gap-2">
                                        {experience.technologies.map((tech, idx) => (
                                            <span
                                                key={idx}
                                                className={`text-xs px-2 py-1 rounded-full ${
                                                    darkMode
                                                        ? 'bg-gray-600 text-gray-200'
                                                        : 'bg-gray-200 text-gray-700'
                                                }`}
                                            >
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>

                {/* Educación */}
                <div>
                    <h3 className={`text-2xl font-semibold mb-4 flex items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <FaGraduationCap className={`mr-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        {t('experience.sections.education')}
                    </h3>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {educationData.map(education => (
                            <motion.div
                                key={education.id}
                                variants={itemVariants}
                                className={`rounded-lg p-5 border-l-4 ${
                                    darkMode
                                        ? 'bg-gray-700 border-cyan-400'
                                        : 'bg-gray-50 border-cyan-500'
                                }`}
                            >
                                <div className="flex flex-col md:flex-row md:justify-between mb-2">
                                    <h4 className="text-xl font-bold">{education.title}</h4>
                                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {education.period}
                                    </span>
                                </div>

                                <p className={`text-lg mb-2 ${darkMode ? 'text-cyan-300' : 'text-cyan-600'}`}>
                                    {education.institution}
                                </p>

                                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {education.description}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>

                {/* Logros */}
                <div>
                    <h3 className={`text-2xl font-semibold mb-4 flex items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <FaMedal className={`mr-2 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        {t('experience.sections.achievements')}
                    </h3>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {achievementsData.map(achievement => (
                            <motion.div
                                key={achievement.id}
                                variants={itemVariants}
                                className={`rounded-lg p-5 border-l-4 ${
                                    darkMode
                                        ? 'bg-gray-700 border-cyan-400'
                                        : 'bg-gray-50 border-cyan-500'
                                }`}
                            >
                                <h4 className="text-xl font-bold mb-2">{achievement.title}</h4>
                                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {achievement.description}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Experience;